/* eslint-disable no-undef */
/**
 * @fileOverview WebCrypto helpers for API level encryption.
 * AES-256-GCM (12 byte IV, 16 byte tag) and RSA-OAEP (SHA-256, SPKI).
 * @module encryption/crypto
 */

import { ENC_VERSION } from "./config";

const TAG_BYTES = 16;
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const subtle = () => globalThis.crypto.subtle;

export const randomBytes = (length) =>
  globalThis.crypto.getRandomValues(new Uint8Array(length));

/** Uint8Array -> standard Base64 with padding. */
export const bytesToBase64 = (bytes) => {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
};

/** Standard Base64 -> Uint8Array. */
export const base64ToBytes = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/** 16 random bytes as base64url without padding (22 chars). */
export const newRid = () =>
  bytesToBase64(randomBytes(16))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

/** URL pathname only, `//` collapsed and trailing `/` stripped. */
export const normalizePath = (url) => {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  let { pathname } = new URL(url, origin);
  pathname = pathname.replace(/\/{2,}/g, "/");
  if (pathname.length > 1 && pathname.endsWith("/")) {
    pathname = pathname.slice(0, -1);
  }
  return pathname;
};

/** BFENC|1|<sid or "-">|<rid>|<ts>|<METHOD>|<path>|<req|res> */
export const makeAad = (sid, rid, ts, method, path, direction) =>
  textEncoder.encode(
    [
      "BFENC",
      ENC_VERSION,
      sid || "-",
      rid,
      ts,
      method.toUpperCase(),
      path,
      direction,
    ].join("|"),
  );

export const importAesKey = (rawBytes) =>
  subtle().importKey("raw", rawBytes, { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);

/** Encrypts a UTF-8 string, returns Base64 `{ iv, ct, tag }`. */
export const aesEncrypt = async (key, plaintext, aad) => {
  const iv = randomBytes(12);
  const result = new Uint8Array(
    await subtle().encrypt(
      { name: "AES-GCM", iv, additionalData: aad, tagLength: TAG_BYTES * 8 },
      key,
      textEncoder.encode(plaintext),
    ),
  );
  const split = result.length - TAG_BYTES;
  return {
    iv: bytesToBase64(iv),
    ct: bytesToBase64(result.subarray(0, split)),
    tag: bytesToBase64(result.subarray(split)),
  };
};

/** Decrypts Base64 `{ iv, ct, tag }` back into a UTF-8 string. */
export const aesDecrypt = async (key, { iv, ct, tag }, aad) => {
  const cipher = base64ToBytes(ct);
  const tagBytes = base64ToBytes(tag);
  const combined = new Uint8Array(cipher.length + tagBytes.length);
  combined.set(cipher, 0);
  combined.set(tagBytes, cipher.length);
  const plain = await subtle().decrypt(
    {
      name: "AES-GCM",
      iv: base64ToBytes(iv),
      additionalData: aad,
      tagLength: TAG_BYTES * 8,
    },
    key,
    combined,
  );
  return textDecoder.decode(plain);
};

export const importRsaPublicKey = (spkiBytes) =>
  subtle().importKey(
    "spki",
    spkiBytes,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"],
  );

/** RSA-OAEP wraps raw key bytes, returns standard Base64. */
export const rsaWrapKey = async (publicKey, rawKeyBytes) =>
  bytesToBase64(
    new Uint8Array(
      await subtle().encrypt({ name: "RSA-OAEP" }, publicKey, rawKeyBytes),
    ),
  );

/** Lowercase hex of SHA-256(spki DER). */
export const fingerprintHex = async (spkiBytes) =>
  Array.from(new Uint8Array(await subtle().digest("SHA-256", spkiBytes)))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
